import React, { MouseEvent, useEffect, useState, useRef } from 'react';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { withRouter } from 'react-router';
import {
    matchPath,
    Redirect,
    Route,
    Switch,
    useHistory,
    useLocation,
} from 'react-router-dom';
import isElectron from 'is-electron';
import appConfig from 'app-config';
import HeadsetFinder from 'resources/headsets/headset-finder';
import { twMerge } from 'tailwind-merge';

import {
    clearWebframeCache,
    initializeZoomLevel,
    handleWindowResize,
    setupRouteChangeHandler,
    forceApplyZoomLevel,
} from 'flow/utils/electron-utils';

import 'css/main.css';

import {
    prepareInitialState,
    updateLayoutColumns,
} from 'flow/actions/app-actions';
import {
    registerHeadsetRef,
    unregisterHeadset,
    updateUserStatusFromState,
} from 'flow/actions/headset-actions';
import { showSignin } from 'flow/actions/navigate-actions';
import { getBrand, getBrandKey } from 'flow/brands/brands';
import { isFlowBrand } from 'flow/utils/brand-utils';
import AudioReference from 'flow/components/audio-reference';
import CommunicationCanvas from 'flow/components/call-components/CommunicationCanvas';
import MeetingWaitingRoomAlert from 'flow/components/meeting/MeetingWaitingRoomAlert';
import CallDialogs from 'flow/components/CallDialogs';
import DialpadWrapper from 'flow/components/DialpadWrapper';
import ContentPaneErrorBoundary from 'flow/components/error-boundary/ContentPaneErrorBoundary';
import LeftPaneErrorBoundary from 'flow/components/error-boundary/LeftPaneErrorBoundary';
import Lightbox from 'flow/components/LightBox';
import Navigator from 'flow/components/navigator';
import OmniiFrameWrapper from 'flow/components/omni/OmniiFrameWrapper';
import Snackbar from 'flow/components/Snackbar';
import StateViewer from 'flow/components/StateViewer/StateViewer';
import ThreadMessageWrapper from 'flow/components/thread-messages/ThreadMessageWrapper';
import AppToolbar from 'flow/components/ui-components/AppToolbar';
import Loading from 'flow/components/ui-components/Loading';
import VideoCanvas from 'flow/components/video-canvas/VideoCanvas';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import Divider from 'flow/components/ui-components/Divider';

import CallsRoutes from 'flow/routes/CallsRoutes';
import ExtensionsRoutes from 'flow/routes/ExtensionsRoutes';
import MessagesRoutes from 'flow/routes/MessagesRoutes';
import PbxRoutes from 'flow/routes/PbxRoutes';
import SettingsRoutes from 'flow/routes/SettingsRoutes';
import TicketsRoutes from 'flow/routes/TicketsRoutes';
import VideoRoutes from 'flow/routes/VideoRoutes';
import MainHeader from 'flow/route-components/MainHeader';

import {
    selectAppState,
    selectCallCanvasSize,
    selectIsLoggedIn,
    selectIsSinglePaneMode,
    selectLayoutColumns,
} from 'flow/selectors/app-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectIsHeadsetControlEnabled,
    selectRegisteredHeadsets,
} from 'flow/selectors/headset-selectors';
import { selectOwnUserHasOmnichannelFeature } from 'flow/selectors/own-extension-selectors';
import { routes } from 'flow/types/routes';
import {
    calculateSelectedTab,
    isMobileApp,
    isTeamsTypeIntegration,
} from 'flow/utils/app-utils';
import {
    addHeadsetLogEntry,
    addLocationLogEntry,
} from 'flow/utils/browser-utils';
import { executeAsDeeplink } from 'flow/utils/deeplink-utils';
import { generatePath } from 'flow/utils/router-utils';
import { DisplayMode } from 'flow/utils/rtc-utils';
import CallDigestTranscriptionsRedirect from './CallDigestTranscriptionsRedirect';
import StartView from './StartView';
import useHasGlobalStateViewer from 'flow/hooks/useHasGlobalStateViewer';
import AiStudioPage from 'flow/pages/ai-studio/AiStudioPage';
import { useUserRegionData } from 'flow/hooks/region-lookup/useUserRegionData';
import { useModal } from 'flow/contexts/ModalProvider';
import { REGION_MISMATCH_LOGOUT } from 'flow/constants/region-lookup/login-data';

let headsetInterval: NodeJS.Timeout;

const Main = () => {
    const { embedded, phoneEnabled } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const location = useLocation();
    const { openModal } = useModal();
    const hasGlobalStateViewer = useHasGlobalStateViewer();

    const appState = useAppSelector(selectAppState);
    const layoutColumns = useAppSelector(selectLayoutColumns);
    const preferredCallCanvasSize = useAppSelector(selectCallCanvasSize);
    const loggedIn = useAppSelector(selectIsLoggedIn);
    const isSinglePaneMode = useAppSelector(selectIsSinglePaneMode);
    const { isRegionCheckEnabled, regionData, isRegionInitialized } =
        useUserRegionData();

    const previewImage = useAppSelector((state) =>
        state.chats.get('previewImage'),
    );

    const headsetsEnabled = useAppSelector(selectIsHeadsetControlEnabled);
    const registeredHeadsets = useAppSelector(selectRegisteredHeadsets);

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const userHasOmnichannelFeature = useAppSelector(
        selectOwnUserHasOmnichannelFeature,
    );

    const [showCanvas, setShowCanvas] = useState(false);
    const [showVideoCanvas, setShowVideoCanvas] = useState(false);

    const HEADSET_POLLING_RATE_MS = 10000;
    const CLEAR_CACHE_RATE = 60 * 60 * 1000; // 1 hour
    const PROTOCOL_QUERYPARAM = 'deeplink';

    // Initialize zoom level and set up window resize handler
    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (isElectron()) {
            initializeZoomLevel();

            let resizeTimeout: NodeJS.Timeout;
            const handleResize = () => {
                if (resizeTimeout) {
                    clearTimeout(resizeTimeout);
                }

                resizeTimeout = setTimeout(() => {
                    handleWindowResize();
                }, 100);
            };

            window.addEventListener('resize', handleResize);

            // Force apply zoom level 1 second after component mounts
            // This ensures the zoom is set correctly even if there are initial layout changes
            const forceApplyTimeout = setTimeout(() => {
                forceApplyZoomLevel();
            }, 1000);

            return () => {
                window.removeEventListener('resize', handleResize);
                if (resizeTimeout) {
                    clearTimeout(resizeTimeout);
                }
                clearTimeout(forceApplyTimeout);
            };
        }
    }, []);

    const prevPathRef = useRef(location.pathname);

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (isElectron() && prevPathRef.current !== location.pathname) {
            setupRouteChangeHandler();
            prevPathRef.current = location.pathname;

            // Force apply zoom level 300ms after route change
            // This helps ensure consistent zoom after the new route components render
            const forceApplyTimeout = setTimeout(() => {
                forceApplyZoomLevel();
            }, 300);

            return () => {
                clearTimeout(forceApplyTimeout);
            };
        }
    }, [location.pathname]);

    useEffect(() => {
        if (appState === 'boot' && isRegionInitialized) {
            dispatch(
                prepareInitialState({
                    isRegionCheckEnabled,
                    regionData,
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRegionInitialized]);

    useEffect(() => {
        if (appState === REGION_MISMATCH_LOGOUT && isRegionCheckEnabled) {
            openModal('userRegionSelectorModal');
            dispatch(showSignin());
        }
    }, [appState, dispatch, openModal, isRegionCheckEnabled]);

    useEffect(() => {
        // The ucaas:// iframe hands off from a browser tab to the desktop
        // app; running it from inside Electron would just re-trigger our
        // own protocol handler and hang the app.
        if (isElectron()) {
            return;
        }
        const queryParams = new URLSearchParams(location.search);
        const deeplink = queryParams.get(PROTOCOL_QUERYPARAM);
        if (deeplink) {
            const path = location.pathname + location.search;
            executeAsDeeplink(path);
        }
    }, [location.pathname, location.search]);

    useEffect(() => {
        let clearCacheInterval: NodeJS.Timeout;
        if (isElectron()) {
            clearCacheInterval = setInterval(
                clearWebframeCache,
                CLEAR_CACHE_RATE,
            );
        }
        return () => {
            if (clearCacheInterval) {
                clearInterval(clearCacheInterval);
            }
        };
    }, [CLEAR_CACHE_RATE]);

    useEffect(() => {
        dispatch(updateLayoutColumns());
    }, [dispatch]);

    useEffect(() => {
        const unlisten = history.listen((location) => {
            addLocationLogEntry(location.pathname);
        });

        return () => {
            unlisten();
        };
    }, [history]);

    useEffect(() => {
        const findHeadset = () => {
            HeadsetFinder.findHeadset()
                .then((headset) => {
                    // We have our headset, stop polling
                    clearInterval(headsetInterval);
                    headsetInterval = null;
                    dispatch(registerHeadsetRef(headset));
                    dispatch(updateUserStatusFromState());
                })
                .catch((err) =>
                    addHeadsetLogEntry('Failed to connect to headset:', err),
                );
        };

        if (
            loggedIn &&
            phoneEnabled &&
            headsetsEnabled &&
            !embedded &&
            registeredHeadsets.isEmpty() &&
            !headsetInterval
        ) {
            headsetInterval = setInterval(findHeadset, HEADSET_POLLING_RATE_MS);
            findHeadset();
        }

        if (!headsetsEnabled) {
            if (headsetInterval) {
                clearInterval(headsetInterval);
                headsetInterval = null;
            }
            registeredHeadsets.forEach((headset) =>
                dispatch(unregisterHeadset(headset.id)),
            );
        }
    }, [
        dispatch,
        embedded,
        headsetsEnabled,
        loggedIn,
        phoneEnabled,
        registeredHeadsets,
    ]);

    // On unmount
    useEffect(
        () => {
            //We don't want to unregister our headsets if anything else than headsetsEnabled changes, so this is only for unmount
            return () => {
                registeredHeadsets.forEach((headset) =>
                    dispatch(unregisterHeadset(headset.id)),
                );
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    if (!loggedIn) {
        const overrideSpinnerColor =
            !isElectron() && isFlowBrand(getBrandKey())
                ? getBrand().theme.palette.greyscale.ornament
                : undefined;
        return (
            <div className="flex size-full">
                <Loading overrideColor={overrideSpinnerColor} />
            </div>
        );
    }

    const defaultTab = calculateSelectedTab(location);

    const fullscreenCall = layoutColumns === 0 ? 'fullscreen' : 'regular';

    const pathToMatch =
        (defaultTab !== 'settings' && defaultTab) || 'settings/:entityKey';

    const match = matchPath(location.pathname, {
        path: '/' + pathToMatch,
        exact: true,
        strict: false,
    });

    const viewClass = (() => {
        if (defaultTab === 'omni' || defaultTab === 'ai-studio') {
            return 'onlyContent';
        }

        if (isSinglePaneMode) {
            if (!match) {
                return 'onlyContent';
            }
            return 'onlyNav';
        }

        return '';
    })();

    const entityKey = location.pathname.split('/')[2];
    const setting = location.pathname.split('/')[3];

    const getOmniChannelComponent = (tab: string) => {
        if (!isMobileApp(embedded) && userHasOmnichannelFeature) {
            return (
                <div
                    className={twMerge(
                        'relative w-full overflow-hidden',
                        tab === 'omni' ? 'block' : 'hidden',
                    )}
                >
                    <OmniiFrameWrapper />
                </div>
            );
        }

        return null;
    };

    const handleContextMenu = (e: MouseEvent) => {
        if (!appConfig.production) {
            return;
        }

        const disableContextMenu = (e.target as HTMLElement).closest(
            '[data-context-menu="false"]',
        );

        if (disableContextMenu) {
            e.preventDefault();
        }
    };

    return (
        <div
            className={twMerge(
                'flex size-full',
                preferredCallCanvasSize === DisplayMode.THUMBNAIL.key ||
                    showVideoCanvas ||
                    (!showCanvas &&
                        preferredCallCanvasSize === DisplayMode.THUMBNAIL.key)
                    ? ''
                    : 'flex-col',
            )}
            onContextMenu={handleContextMenu}
        >
            <div className="flex size-full overflow-x-auto">
                <div
                    className={twMerge(
                        'flex w-full grow flex-col overflow-x-auto',
                        fullscreenCall === 'fullscreen' && 'hidden',
                    )}
                >
                    {hasGlobalStateViewer &&
                        localStorage.getItem('show-global-state-viewer') ===
                            'true' && <StateViewer />}

                    <AudioReference />
                    <MainHeader />
                    <div
                        className={twMerge(
                            'flex shrink grow overflow-hidden',
                            'columns-' + layoutColumns,
                        )}
                    >
                        <div
                            className={twMerge(
                                'leftnav relative flex w-[360px] min-w-[276px] shrink-0 flex-col bg-gray-100',
                                viewClass === 'onlyContent' && 'hidden',
                                viewClass === 'onlyNav' && 'grow',
                            )}
                        >
                            <LeftPaneErrorBoundary tab={defaultTab}>
                                <Navigator
                                    defaultTab={defaultTab}
                                    setting={setting}
                                    entityKey={entityKey}
                                />
                            </LeftPaneErrorBoundary>
                        </div>
                        <div
                            className={twMerge(
                                'relative flex flex-auto',
                                // The tickets route manages its own internal
                                // scrollable regions; letting the main pane
                                // also scroll causes a horizontal width shift
                                // when its scrollbar appears.
                                location?.pathname?.startsWith('/tickets')
                                    ? 'overflow-hidden'
                                    : 'overflow-y-auto',
                                viewClass === 'onlyNav' && 'hidden',
                            )}
                        >
                            <ContentPaneErrorBoundary>
                                <Switch>
                                    <Route
                                        path="/extensions"
                                        component={ExtensionsRoutes}
                                    />
                                    <Route
                                        path="/messages"
                                        component={MessagesRoutes}
                                    />
                                    <Route path="/pbx" component={PbxRoutes} />
                                    <Route
                                        path="/calls"
                                        component={CallsRoutes}
                                    />
                                    <Route path="/omni" />
                                    <Route
                                        path="/video"
                                        component={VideoRoutes}
                                    />
                                    <Route
                                        path="/tickets"
                                        component={TicketsRoutes}
                                    />
                                    <Route
                                        exact={true}
                                        path="/settings/account/transcriptions"
                                        component={
                                            CallDigestTranscriptionsRedirect
                                        }
                                    />
                                    <Route
                                        path={[routes['settings']]}
                                        component={SettingsRoutes}
                                    />
                                    <Route
                                        path={[routes['ai-studio.page']]}
                                        component={AiStudioPage}
                                    />
                                    <Redirect
                                        exact={true}
                                        from="/settings"
                                        to={generatePath(routes['settings'], {
                                            extensionKey: ownExtensionKey,
                                        })}
                                    />

                                    <Route path="/" component={StartView} />
                                </Switch>
                                {getOmniChannelComponent(defaultTab)}
                            </ContentPaneErrorBoundary>
                        </div>
                    </div>
                    <Snackbar />
                    {previewImage && <Lightbox imageSrc={previewImage} />}
                    <CallDialogs />

                    {isTeamsTypeIntegration(embedded) && (
                        <>
                            <Divider />
                            <AppToolbar />
                        </>
                    )}
                </div>
                <DialpadWrapper
                    showCanvas={showCanvas}
                    showVideoCanvas={showVideoCanvas}
                />
                {!isMobileApp(embedded) ? (
                    <ThreadMessageWrapper
                        showCanvas={showCanvas}
                        showVideoCanvas={showVideoCanvas}
                        setShowVideoCanvas={setShowVideoCanvas}
                    />
                ) : null}
            </div>
            {!isMobileApp(embedded) ? (
                <>
                    <CommunicationCanvas setShowCanvas={setShowCanvas} />

                    <VideoCanvas
                        showCanvas={showCanvas}
                        showVideoCanvas={showVideoCanvas}
                        setShowVideoCanvas={setShowVideoCanvas}
                    />
                </>
            ) : null}

            {/* No UI of its own: sounds the waiting-room alert wherever the
                user happens to be, which is why it sits out here rather than
                inside a canvas — those are behind the isMobileApp guard above,
                and a knock has to be heard on mobile too. A leaf, so the list
                changing does not re-render the shell. */}
            <MeetingWaitingRoomAlert />
        </div>
    );
};

const ConnectedComponent = withRouter(Main);
export default DragDropContext(HTML5Backend)(ConnectedComponent);
